<template>
	<div>
		<div class="Playlist-listen">
			<button class="Button Button--pill Button--huge Button--highlight" @click="open" ref="listen">
				<CloIcon symbol="audio" />
				{{ $t('species.listen') }}
			</button>
			<button v-if="list.length > 1" class="Playlist-listenMore" @click="open">
				+
				<span v-if="list.length > 2">{{ $t('species.moreAudioRecordings', [list.length - 1]) }}</span>
				<span v-else>{{ $t('species.moreAudioRecording', [1]) }}</span>
			</button>
			<a v-if="moreUrl && list.length === 1" :href="moreUrl">{{ $t('species.moreAudio') }}</a>
		</div>
		<transition name="fade">
			<div v-if="active" class="Playlist-backdrop" @click="close"></div>
		</transition>
		<transition name="slideUp">
			<div
				v-if="active"
				class="Playlist Playlist--reverse"
				:class="{ active: open }"
				tabindex="-1"
				role="dialog"
				ref="playlist"
				@keyup.escape.stop.prevent="close"
			>
				<div class="Playlist-header">
					<h1 class="Playlist-heading">{{ title }}</h1>
				</div>
				<div class="Playlist-player">
					<CloSpectrogramPlayer
						v-if="showPlayer"
						class="SpectrogramPlayer--sm SpectrogramPlayer--primary SpectrogramPlayer--withMeta"
						size="sm"
						autoplay
						version="2.0"
						:mediaDownloadUrl="mediaDownloadUrl"
						timeline
						:displayPxPerSec="120"
						:title="player.title"
						:assetId="player.asset.assetId.toString()"
						:citationUrl="player.asset.citationUrl"
						:citationName="player.asset.citationName"
						:user="player.asset.userDisplayName"
						:location="player.asset.location"
						:date="player.asset.obsDtDisplay"
					/>
				</div>
				<div class="Playlist-tracks">
					<button
						v-for="(item, index) in list"
						:key="index"
						class="Button Button--fauxRadio Button--reverse Button--icon Button--left"
						:class="{ active: currentTrack === index }"
						type="button"
						@click="playTrack(index)"
					>
						<CloIcon class="u-inline-sm" symbol="audio" />
						{{ item.title }}
						<span class="Playlist-playingStatus" v-if="currentTrack === index">{{ $t('species.playing') }}</span>
					</button>
				</div>
				<div class="Playlist-footer">
					<button class="Button Button--expanded" type="button" @click="close">{{ $t('generic.Close') }}</button>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import CloButton from '@/base/CloButton/CloButton.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloSpectrogramPlayer from '@/media/CloSpectrogramPlayer/CloSpectrogramPlayer.vue';
import CloLogoMacaulayMobile from '@/logos/CloLogoMacaulayMobile/CloLogoMacaulayMobile.vue';
export default {
	components: { CloButton, CloIcon, CloSpectrogramPlayer, CloLogoMacaulayMobile },
	data() {
		return {
			active: false,
			currentTrack: 0,
			showPlayer: true,
		};
	},
	props: {
		/**
		 * Title for the component
		 */
		title: {
			type: String,
			required: true,
		},
		/**
		 * An array of items as argument for the component
		 */
		list: {
			type: Array,
			required: true,
		},
		/**
		 * The 'MORE' URL
		 */
		moreUrl: {
			type: String,
			default: null,
		},
		mediaDownloadUrl: {
			default: 'https://cdn.download.ams.birds.cornell.edu/api/v2/asset/',
			type: String,
		},
	},
	computed: {
		player() {
			const track = this.list[this.currentTrack];
			const locArray = [];
			if (track.asset.hasOwnProperty('locName') && track.asset.locName) {
				locArray.push(track.asset.locName);
			}
			if (track.asset.hasOwnProperty('subnational2Name') && track.asset.subnational2Name) {
				locArray.push(track.asset.subnational2Name);
			}
			if (track.asset.hasOwnProperty('subnational1Name') && track.asset.subnational1Name) {
				locArray.push(track.asset.subnational1Name);
			}
			if (track.asset.hasOwnProperty('countryName') && track.asset.countryName) {
				locArray.push(track.asset.countryName);
			}
			track.asset.location = locArray.join(', ');
			return track;
		},
	},
	methods: {
		open() {
			this.active = true;
			this.$nextTick(() => {
				this.$refs.playlist.focus();
			});
		},
		close() {
			this.active = false;
			this.$refs.listen.focus();
		},
		playTrack(index) {
			this.showPlayer = false;
			this.currentTrack = index;
			this.$nextTick(() => {
				this.showPlayer = true;
			});
		},
	},
};
</script>

<style lang="scss">
.Playlist {
	background-color: $color-primary-5;
	border-radius: $radius-sm;
	@include dropshadow-2;
	position: fixed;
	top: auto;
	bottom: 0;
	/*@noflip*/
	left: 0;
	/*@noflip*/
	right: 0;
	width: 100%;
	max-height: 100vh;
	z-index: 1007;
	@include breakpoint(medium) {
		top: 50%;
		bottom: auto;
		/*@noflip*/
		left: 50%;
		/*@noflip*/
		right: auto;
		max-width: 60rem;
		min-width: 40vw;
		max-height: 100vh;
		min-height: 20rem;
	}
	transform: translate(0, 0);
	@include breakpoint(medium) {
		transform: translate(-50%, -50%);
	}
	&.slideUp-enter-active,
	&.slideUp-leave-active {
		transition: transform 0.4s;
	}
	&.slideUp-enter,
	&.slideUp-leave-to {
		transform: translate(0, 100%);
		@include breakpoint(medium) {
			transform: translate(-50%, 50%);
		}
	}
	display: flex;
	flex-direction: column;
	.Playlist-header {
		flex: 1 0 auto;
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
	}
	.Playlist-heading {
		flex: 1 0 auto;
		font-size: $text-size-sm;
		margin-bottom: 0;
		@include inset-sm;
	}
	.Playlist-branding {
		@include inset-sm;
	}
	.Playlist-player {
		flex-shrink: 0;
	}
	.Playlist-tracks {
		display: flex;
		flex-direction: column;
		flex: 0 1 auto;
		overflow-y: auto;
		.Button {
			margin-bottom: -1px;
			color: $color-link-reverse;
			.Playlist-playingStatus {
				font-size: $text-size-xs;
				font-weight: normal;
				text-transform: uppercase;
				margin-left: $space-md;
			}
		}
		.Button.active {
			color: $color-white;
		}
	}
	.Playlist-footer {
		border-top: 1px solid $color-neutral-4;
		text-align: center;
		@include inset-md;
		.Button {
			margin-bottom: 0;
		}
	}
}

.Playlist-backdrop {
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: $color-overlay-black;
	&.fade-enter-active,
	&.fade-leave-active {
		transition: opacity 0.2s;
	}
	&.fade-enter,
	&.fade-leave-to {
		opacity: 0;
	}
}

.Playlist--reverse {
	background-color: $color-primary-5;
	.Playlist-heading {
		color: $color-white;
	}
}

.Playlist-listen {
	display: inline-flex;
	flex-direction: column;
	text-align: center;
	gap: $space-sm;
	.Playlist-listenMore {
		display: none;
		font-size: $text-size-sm;
		color: $color-link;
	}
	@include breakpoint(medium) {
		background-color: $color-primary-0;
		@include inset-md;
		.Playlist-listenMore {
			display: block;
		}
	}
}
</style>
